/**
 * One product in the catalogue list.
 *
 * Shows the product photo (or a placeholder) so the list reads like a menu of what's for
 * sale. The QR code lives behind the options menu rather than on the tile — organizers
 * recognise their products by sight, not by QR pattern.
 */
import React from "react";
import { useTranslation } from "react-i18next";

import defaultImage from "../../assets/defaultImage.svg";
import { TwListMoreButton, TwMoreOptions } from "../../components/tailwind";

export default function ProductTile({
  product,
  onEdit,
  onShowQr,
  onDownloadQr,
  onArchive,
}) {
  const { t } = useTranslation();
  const inactive = product?.isActive === false;

  return (
    <li
      className={`flex items-center gap-4 rounded-xl border p-3 ${
        inactive ? "border-gray-200 bg-gray-50" : "border-gray-200 bg-white"
      }`}
    >
      <img
        src={product?.photo || defaultImage}
        alt={product?.name || ""}
        onError={(e) => {
          e.currentTarget.src = defaultImage;
        }}
        className={`size-16 rounded-lg object-cover bg-gray-100 shrink-0 ${
          inactive ? "opacity-40" : ""
        }`}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-semibold truncate">{product?.name}</p>
          {inactive ? (
            <span className="shrink-0 text-[11px] uppercase tracking-wide text-gray-500 border border-gray-300 rounded px-1.5 py-0.5">
              {t("products.inactive", "Paused")}
            </span>
          ) : null}
        </div>

        <p className="text-sm text-gray-500">
          €{parseFloat(product?.onSitePrice || 0).toFixed(2)}
        </p>

        {product?.description ? (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
            {product.description}
          </p>
        ) : null}
      </div>

      <div className="flex flex-none items-center gap-x-4">
        <TwMoreOptions
          options={[
            <TwListMoreButton
              className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
              onClick={onEdit}
            >
              {t("inputFieldsTitle.edit", "Edit")}
            </TwListMoreButton>,
            <TwListMoreButton
              className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
              onClick={onShowQr}
            >
              {t("products.qrCode", "QR code")}
            </TwListMoreButton>,
            <TwListMoreButton
              className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
              onClick={onDownloadQr}
            >
              {t("products.downloadQr", "Download QR")}
            </TwListMoreButton>,
            <TwListMoreButton
              className="block px-3 py-1 text-sm/6 text-red-600 data-[focus]:bg-gray-50 data-[focus]:outline-none"
              onClick={onArchive}
            >
              {t("products.archive", "Archive")}
            </TwListMoreButton>,
          ]}
        />
      </div>
    </li>
  );
}
